// Typed route guards / middleware for the route tree in src/router.ts.
//
// The guard *decisions* live here, fully type-checked, and are consumed from the
// `beforeLoad` hooks in router.ts. Keeping the logic in this module means the auth
// gate and its redirect targets are type-safe and unit-testable in isolation from
// the router's runtime API.
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { db } from '../services/db';
import { isCreatorProfileComplete } from './creatorProfile';
import type { AppRoutePath, SearchFor } from './routes';

/** The subset of a route `beforeLoad` location a guard needs. */
export interface GuardLocation {
  /** Fully-qualified href of the location being navigated to. */
  href: string;
}

/**
 * A redirect a guard asks the router to perform, decoupled from the router's own
 * `redirect()` API so guard logic can be tested without importing @tanstack/react-router.
 * The `to` path drives the type of `search` via {@link SearchFor}.
 */
export interface RedirectInstruction<P extends AppRoutePath = AppRoutePath> {
  to: P;
  search?: SearchFor<P>;
  hash?: string;
}

/** Shape of `supabase.auth.getSession()`'s resolved value that we rely on. */
interface GetSessionResult {
  data: { session: Session | null };
}

/**
 * Auth gate for the authenticated route subtree.
 *
 * Returns `null` when access is allowed — either a live session exists, or the app
 * is running in demo mode with Supabase unconfigured — and a redirect to the auth
 * page (carrying the attempted URL as `redirect` search param) otherwise.
 */
export async function resolveAuthedGuard(
  location: GuardLocation
): Promise<RedirectInstruction<'/auth'> | null> {
  // Demo mode: no Supabase client, so every authed page renders freely.
  if (!supabase) return null;
  const { data } = (await supabase.auth.getSession()) as GetSessionResult;
  if (data?.session) return null;
  return { to: '/auth', search: { redirect: location.href } };
}

/**
 * Gate for the creator profile builder (PIT-48).
 *
 * Returns `null` when the builder should render — the creator has no row yet,
 * or has a partially-built one to resume — and a redirect to the dashboard once
 * their profile carries everything onboarding collects, so a finished creator
 * can't wander back into onboarding and can't be asked to build a second
 * profile. Demo mode always renders the flow: there is no backend to own a row,
 * and `getMyCreator()` stands in the bundled showcase profile, which would
 * otherwise bounce every visit straight back out.
 */
export async function resolveOnboardingGuard(): Promise<RedirectInstruction<'/creator'> | null> {
  if (!supabase) return null;
  try {
    const row = await db.getMyCreator();
    return isCreatorProfileComplete(row) ? { to: '/creator' } : null;
  } catch {
    // A failed lookup must not lock a creator out of onboarding — render the
    // builder; its first save resolves the row idempotently either way.
    return null;
  }
}

/**
 * Redirect for the `/waitlist` short URL used in social bios: it has no page of
 * its own and bounces to the landing page's `#waitlist` anchor.
 */
export const WAITLIST_REDIRECT: RedirectInstruction<'/'> = { to: '/', hash: 'waitlist' };
